"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Eye, Book, Palette, Star, Zap, Crown, Brain, Sparkles, Users } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function ThothTarotPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Current Section Indicator */}
            <div className="mb-4 p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-indigo-300">
                <Eye className="h-4 w-4" />
                <span className="text-sm font-medium">🃏 Tarot</span>
                <span className="text-indigo-500">/</span>
                <span className="text-sm">Sistemler</span>
                <span className="text-indigo-500">/</span>
                <span className="text-sm">Thoth Tarot</span>
              </div>
            </div>

            {/* Breadcrumb */}
            <div className="mb-6">
              <Link
                href="/about"
                className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                AXTROLOG Nedir
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/tarot/systems"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Tarot Sistemleri
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <span className="text-slate-300">Thoth Tarot</span>
            </div>

            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-900/20 to-purple-900/20 rounded-xl p-8 mb-8 border border-indigo-500/20">
              <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <Eye className="h-10 w-10 text-indigo-400" />
                Thoth Tarot
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed">
                Aleister Crowley ve Lady Frieda Harris tarafından yaratılan derin ezoterik içerikli tarot sistemi.
                Thelema felsefesi, bilim, felsefe ve çeşitli okült sistemlerin sentezi.
              </p>
            </div>

            {/* Deck Structure */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Deste Yapısı</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Major Arcana */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-700 rounded-lg flex items-center justify-center">
                      <Star className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Majör Arkana (22 Kart)</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    Aleister Crowley'nin ezoterik görüşleri ve Lady Frieda Harris'in sanatsal yorumu
                    ile yeniden tasarlanmış büyük sırlar. Geleneksel tarot'tan farklı adlandırma ve düzenleme.
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Trump kartları yeniden adlandırıldı</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Thelema felsefesi ile uyumlu</span>
                    </div>
                  </div>
                  <Link
                    href="/about/tarot/systems/thoth/major-arcana"
                    className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm"
                  >
                    <Star className="h-4 w-4 mr-2" />
                    22 Majör Arkana Kartını Gör
                  </Link>
                </div>

                {/* Minor Arcana */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-700 rounded-lg flex items-center justify-center">
                      <Book className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Minör Arkana (56 Kart)</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    Aleister Crowley'nin ezoterik görüşleri ve Lady Frieda Harris'in sanatsal yorumu
                    ile yeniden tasarlanmış günlük yaşam kartları. Court kartları benzersiz isimlendirme sistemine sahip.
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Wands, Cups, Swords, Disks</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Court kartları: Prince, Princess, Knight, Queen</span>
                    </div>
                  </div>
                  <Link
                    href="/about/tarot/systems/thoth/minor-arcana"
                    className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
                  >
                    <Book className="h-4 w-4 mr-2" />
                    56 Minör Arkana Kartını Gör
                  </Link>
                </div>
              </div>
            </div>

            {/* Creators */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaratıcılar ve Gelişim Süreci</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Aleister Crowley */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-700 rounded-lg flex items-center justify-center">
                      <Book className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Aleister Crowley (Konsept)</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    İngiliz okültist ve yazar. Thelema felsefesinin kurucusu. Desteyi "The Book of Thoth"
                    olarak adlandırdı ve derin felsefi temellere oturttu.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Thelema felsefesinin yaratıcısı</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">"Book of Thoth" kitabının yazarı</span>
                    </div>
                  </div>
                </div>

                {/* Lady Frieda Harris */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-700 rounded-lg flex items-center justify-center">
                      <Palette className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Lady Frieda Harris (Sanatçı)</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    İngiliz sanatçı ve aristokrat. Suluboya kağıdı üzerinde el çizimi kartları yarattı.
                    Orijinal eserler 2011'de konservasyon geçirdi ve Warburg Enstitüsü'nde saklanıyor.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-rose-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Suluboya tekniği ustası</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-rose-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">5 yıllık dedicated çalışma (1938-1943)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Development Timeline */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Gelişim Tarihi</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-slate-700 rounded-lg">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-700 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <Sparkles className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-2">Başlangıç (1938)</h3>
                    <p className="text-sm text-slate-400">
                      6 aylık proje olarak planlandı. Crowley ve Harris ortaklığı başladı.
                    </p>
                  </div>

                  <div className="text-center p-4 bg-slate-700 rounded-lg">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-700 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <Palette className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-2">Yaratım (1938-1943)</h3>
                    <p className="text-sm text-slate-400">
                      5 yıl süren intensif sanatsal ve felsefi çalışma dönemi.
                    </p>
                  </div>

                  <div className="text-center p-4 bg-slate-700 rounded-lg">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-700 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <Book className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-2">Konservasyon (2011)</h3>
                    <p className="text-sm text-slate-400">
                      Orijinal suluboya kartlar restore edildi ve korunma altına alındı.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Philosophical Foundations */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Felsefi Temeller</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Thelema Philosophy */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-700 rounded-lg flex items-center justify-center">
                      <Star className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Thelema Felsefesi</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    Crowley'nin geliştirdiği spiritual felsefe. "Do what thou wilt shall be the
                    whole of the Law" prensibini temel alır.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-violet-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">True Will (Gerçek İrade) kavramı</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-violet-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Aeon of Horus (Horus Çağı)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-violet-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Kişisel spiritual gelişim odağı</span>
                    </div>
                  </div>
                </div>

                {/* Multi-disciplinary Synthesis */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-700 rounded-lg flex items-center justify-center">
                      <Brain className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Multidisipliner Sentez</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    Bilim, felsefe ve çeşitli okült sistemlerden sembolizm
                    birleştiren kapsamlı yaklaşım.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Modern bilim ve matematik</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Çeşitli okült sistemler</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Felsefi ve psikolojik anlayış</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Unique Characteristics */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Benzersiz Özellikler</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3">Geleneksel Tarot'tan Farklar</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Trump kartları yeniden adlandırıldı ve düzenlendi</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Astrolojik correspondence'lar değiştirildi</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">İbranice alfabe bağlantıları modifiye edildi</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Court kartları benzersiz isimlendirme yapısı</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3">Deste Varyasyonları</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Altı majör versiyon mevcut</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Baskı renklerinde farklılıklar</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Kart kenarlıklarında değişiklikler</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Sembolik elementlerde varyasyonlar</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Artistic Features */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Sanatsal Özellikler</h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-700 rounded-lg flex items-center justify-center">
                      <Palette className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Suluboya Teknik</h3>
                  </div>
                  <p className="text-slate-300 text-sm">
                    Lady Harris tarafından suluboya kağıdı üzerinde el çizimi.
                    Yoğun renkler ve karmaşık geometrik desenler.
                  </p>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-700 rounded-lg flex items-center justify-center">
                      <Zap className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Sembolik Karmaşıklık</h3>
                  </div>
                  <p className="text-slate-300 text-sm">
                    Her kartta multiple katmanlı sembolizm.
                    Matematiksel, astrolojik ve okült referanslar.
                  </p>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-700 rounded-lg flex items-center justify-center">
                      <Crown className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Estetik İnovasyon</h3>
                  </div>
                  <p className="text-slate-300 text-sm">
                    Art Deco ve modern sanat etkileri.
                    Geleneksel tarot ikonografisinin radikal yeniden yorumu.
                  </p>
                </div>
              </div>
            </div>

            {/* Book of Thoth */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">"The Book of Thoth" Kitabı</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <p className="text-slate-300 mb-4">
                  Crowley'nin desteye eşlik eden kapsamlı kitabı. Her kartın detaylı açıklaması,
                  felsefi temelleri ve pratik kullanım rehberi içerir.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <h4 className="font-semibold text-slate-200 mb-2">İçerik:</h4>
                    <ul className="text-sm text-slate-400 space-y-1">
                      <li>• Her kartın detaylı sembolik analizi</li>
                      <li>• Thelema felsefesi ile bağlantılar</li>
                      <li>• Divination teknikleri ve yöntemleri</li>
                      <li>• Kabalistik Tree of Life açıklamaları</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-slate-200 mb-2">Önem:</h4>
                    <ul className="text-sm text-slate-400 space-y-1">
                      <li>• Modern ezoterik tarot teorisinin temeli</li>
                      <li>• Crowley'nin okült öğretilerinin sentezi</li>
                      <li>• İleri düzey tarot çalışması için referans</li>
                      <li>• Akademik okült çalışmalarda kaynak</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Note */}
            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
              <p className="text-amber-100 leading-relaxed">
                Thoth Tarot derin felsefi ve sembolik bir çalışmadır ve kesinlik iddiası taşımaz.
                İçerikler tamamen merak, eğlence ve kişisel farkındalık amaçlıdır. Yaşamsal kararlar için
                uzman görüşü alınmalıdır. Bu açıklamalar sadece tarihi ve sanatsal değerin tanıtımı amacıyla sunulmaktadır.
              </p>
            </div>

            {/* Main Tarot Training CTA */}
            <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-xl p-6 mb-8">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="text-2xl font-semibold text-slate-100 mb-3">🎴 Tarot Eğitimleri</h3>
                <p className="text-slate-300 mb-6 leading-relaxed max-w-2xl mx-auto">
                  Tarot kart sistemleri, sembolik yorumlama teknikleri, kart dizilimleri ve okuma yöntemleri hakkında
                  detaylı eğitim programları için kayıt olun. Merak ettiğiniz konularda rehberlik alın.
                </p>
                <button className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-lg transition-all transform hover:scale-105 shadow-lg">
                  <Users className="h-5 w-5 mr-2" />
                  Tarot Eğitimi İçin Kayıt Ol
                </button>
                <p className="text-slate-400 text-sm mt-3">
                  * Eğitim programları sadece bilgilendirme ve kişisel gelişim amaçlıdır
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Link
                href="/about/tarot/systems/marseille"
                className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Tarot de Marseille
              </Link>

              <Link
                href="/about/tarot/systems/golden-dawn"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Golden Dawn Tarot
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Link>
            </div>
      </div>
    </AboutLayout>
  );
}